import { useState, useCallback, useEffect, useRef } from 'react';
import { Message, ChatConfig, ChatState, AgentType, WorkflowNode } from './types';
import { ChatAPIClient } from './chat-api-client';
import { ChatAPI } from './chat-api';

function formatErrorMessage(raw: string): string {
  if (/timeout|ETIMEDOUT|ECONNABORTED/i.test(raw)) return 'Request timed out. Please try again.';
  if (/5\d{2}|internal server/i.test(raw)) return 'Request failed. Server is not responding. Wait a moment and try again.';
  if (/4\d{2}|bad request|unauthorized|forbidden/i.test(raw)) return 'Request failed. Please check your input and try again.';
  if (/network|ECONNREFUSED|fetch/i.test(raw)) return 'Network error. Check your connection and retry.';
  if (/abort/i.test(raw)) return 'Request was cancelled.';
  return raw;
}

export interface UseChatOptions extends ChatConfig {
  /**
   * API client for chat operations (CRUD + streaming)
   * Each app provides its own implementation
   */
  apiClient: ChatAPIClient;
}

export function useChat(config: UseChatOptions) {
  const { apiClient, ...chatConfig } = config;
  
  const defaultMessage: Message = {
    id: 'welcome_message',
    text: 'Hey, how can I help you? 👋',
    sender: 'assistant',
    timestamp: new Date(),
  };

  const getExistingSessionId = useCallback(() => {
    return localStorage.getItem('chat-session-id');
  }, []);

  const [state, setState] = useState<ChatState>({
    isOpen: false,
    messages: [],
    isLoading: false,
    sessionId: '',
    title: '',
    selectedAgent: null,
  });

  const [availableChats, setAvailableChats] = useState<any[]>([]);
  const [isSwitchingChat, setIsSwitchingChat] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [streamingNodes, setStreamingNodes] = useState<WorkflowNode[]>([]);
  const abortStreamRef = useRef<(() => void) | null>(null);

  // ChatAPI for analytics/legacy webhook (default)
  const chatAPI = new ChatAPI(
    chatConfig.webhookUrl,
    chatConfig.chatInputKey,
    chatConfig.chatSessionKey
  );

  // ChatAPI for platform-help webhook (separate n8n workflow)
  const platformHelpAPI = new ChatAPI(
    chatConfig.platformHelpWebhookUrl || chatConfig.webhookUrl,
    chatConfig.chatInputKey,
    chatConfig.chatSessionKey
  );

  /**
   * Transform stored message format to UI format
   * Handles both n8n format and our API format
   */
  const transformMessages = useCallback((storedMessages: any[]): Message[] => {
    return storedMessages.map((msg, index) => {
      // Our API format: { type: 'human'|'ai', data: { content: '...', additional_kwargs: {...} } }
      if (msg.type && msg.data?.content) {
        const base: Message = {
          id: `msg_${index}_${Date.now()}`,
          text: msg.data.content,
          sender: msg.type === 'human' ? 'user' : 'assistant',
          timestamp: new Date(),
        };

        // Restore metadata from additional_kwargs for AI messages
        if (msg.type === 'ai' && msg.data.additional_kwargs) {
          const ak = msg.data.additional_kwargs;
          if (ak.confidenceScore != null) base.confidenceScore = ak.confidenceScore;
          if (ak.uncertaintyReasons) base.uncertaintyReasons = ak.uncertaintyReasons;
          if (ak.tokenUsage) base.tokenUsage = ak.tokenUsage;

          if (ak.keyInsights?.length) {
            base.keyInsights = ak.keyInsights;
          }
          if (ak.recommendedActions?.length) {
            base.recommendedActions = ak.recommendedActions;
          }
          if (ak.workflowNodes?.length) {
            base.workflowNodes = ak.workflowNodes;
          }
        }

        return base;
      }
      // Already in UI format or n8n format
      return msg as Message;
    });
  }, []);

  const loadPreviousSession = useCallback(async () => {
    if (state.sessionId) {
      try {
        setState(prev => ({ ...prev, isLoading: true }));

        // Load from API client
        const chatData = await apiClient.getChatById(state.sessionId);

        if (chatData && chatData.messages && Array.isArray(chatData.messages) && chatData.messages.length > 0) {
          const messages = transformMessages(chatData.messages);
          setState(prev => ({
            ...prev,
            messages,
            _id: chatData._id || prev._id,
            isLoading: false,
          }));
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Failed to load previous session:', error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    }
  }, [state.sessionId, transformMessages, apiClient]);

  useEffect(() => {
    const initializeSession = async () => {
      const sessionId = getExistingSessionId();
      if (sessionId) {
        setState(prev => ({ ...prev, sessionId }));
      }
      
      try {
        const chats = await apiClient.getChats();
        setAvailableChats(chats);
      } catch (error) {
        console.error('Failed to load available chats:', error);
      }
    };
    initializeSession();
  }, [getExistingSessionId, apiClient]);

  const [sessionInitialized, setSessionInitialized] = useState(false);

  useEffect(() => {
    if (state.sessionId && !sessionInitialized) {
      loadPreviousSession();
      setSessionInitialized(true);
    }
  }, [state.sessionId, sessionInitialized, loadPreviousSession]);

  const abortActiveStream = useCallback(() => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    setStreamingNodes([]);
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  const toggleChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, [state.isOpen]);

  const closeChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    const isFirstMessage = state.messages.length === 0;

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
    }));

    try {
      const baseMetadata = chatConfig.getMetadata ? chatConfig.getMetadata() : {};
      const metadata = {
        ...baseMetadata,
        selectedAgent: state.selectedAgent
      };

      let titlePromise: Promise<{ title: string }> | null = null;

      if (isFirstMessage && state.sessionId && state.selectedAgent !== 'platform-help') {
        chatAPI
          .generateChatTitle(text)
          .then(async (titleResponse) => {
            if (titleResponse.title) {
              const trimmedTitle = titleResponse.title.length > 25
                ? titleResponse.title.substring(0, 25) + '...'
                : titleResponse.title;

              try {
                const chatIdentifier = state._id || state.sessionId!;
                await apiClient.updateChat(chatIdentifier, { title: titleResponse.title });
              } catch (updateError) {
                console.error('Failed to update chat title in database:', updateError);
              }

              setAvailableChats(prevChats => {
                const existingChatIndex = prevChats.findIndex(chat => chat.sessionId === state.sessionId);
                if (existingChatIndex >= 0) {
                  const updatedChats = [...prevChats];
                  updatedChats[existingChatIndex] = { ...updatedChats[existingChatIndex], title: trimmedTitle };
                  return updatedChats;
                } else {
                  const newChat = {
                    sessionId: state.sessionId,
                    title: trimmedTitle,
                    agentType: state.selectedAgent,
                    messages: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    _id: `temp_${state.sessionId}`,
                    __v: 0
                  };
                  return [...prevChats, newChat];
                }
              });
            }
          })
      }

      // Route based on agent type
      if (state.selectedAgent === 'analytics-stream') {
        // SSE streaming for deep analytics
        setStreamingNodes([]);
        const collectedNodes: WorkflowNode[] = [];

        const abort = apiClient.streamAnalytics(text, state.sessionId, {
          onNode: (event) => {
            const { node, timestamp, ...details } = event;
            const wfNode: WorkflowNode = { name: node, timestamp, details };
            collectedNodes.push(wfNode);
            setStreamingNodes([...collectedNodes]);
          },
          onDone: (event) => {
            const assistantMessage: Message = {
              id: `assistant_${Date.now()}`,
              text: event.summary || 'No response generated.',
              keyInsights: event.keyInsights?.length ? event.keyInsights : undefined,
              recommendedActions: event.recommendedActions?.length ? event.recommendedActions : undefined,
              sender: 'assistant',
              timestamp: new Date(),
              confidenceScore: event.confidence,
              uncertaintyReasons: event.uncertaintyReasons,
              tokenUsage: event.tokenUsage,
              workflowNodes: collectedNodes,
            };

            setState(prev => ({
              ...prev,
              messages: [...prev.messages, assistantMessage],
              isLoading: false,
            }));
            setStreamingNodes([]);
            abortStreamRef.current = null;
          },
          onError: (errorMsg) => {
            const errMessage: Message = {
              id: `error_${Date.now()}`,
              text: formatErrorMessage(errorMsg),
              sender: 'assistant',
              timestamp: new Date(),
              isError: true,
            };
            setState(prev => ({
              ...prev,
              messages: [...prev.messages, errMessage],
              isLoading: false,
            }));
            setStreamingNodes([]);
            abortStreamRef.current = null;
          },
        });
        abortStreamRef.current = abort;
        return;
      }

      // Use n8n webhook for platform-help
      const activeAPI = state.selectedAgent === 'platform-help' ? platformHelpAPI : chatAPI;
      const responseText = await activeAPI.sendMessage(text, state.sessionId, metadata, 'sendMessage');
      
      const assistantMessage: Message = {
        id: `assistant_${Date.now()}`,
        text: responseText,
        sender: 'assistant',
        timestamp: new Date(),
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: formatErrorMessage(error instanceof Error ? error.message : 'An error occurred'),
        sender: 'assistant',
        timestamp: new Date(),
        isError: true,
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, errorMessage],
        isLoading: false,
      }));
    }
  }, [chatAPI, platformHelpAPI, state.sessionId, state.selectedAgent, state.messages.length, chatConfig, apiClient]);

  const selectAgent = useCallback(async (agent: AgentType) => {
    if (agent === 'platform-help') {
      const ephemeralId = `ephemeral_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setState(prev => ({ ...prev, sessionId: ephemeralId, selectedAgent: agent, messages: [] }));
      return;
    }
    try {
      const newChat = await apiClient.createChat(agent);
      localStorage.setItem('chat-session-id', newChat.sessionId);
      
      setState(prev => ({
        ...prev,
        sessionId: newChat.sessionId,
        _id: newChat._id,
        selectedAgent: agent,
        messages: [],
      }));
      
      const chats = await apiClient.getChats();
      setAvailableChats(chats);
    } catch (error) {
      console.error('Failed to create new chat session:', error);
      const fallbackSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat-session-id', fallbackSessionId);
      setState(prev => ({
        ...prev,
        sessionId: fallbackSessionId,
        selectedAgent: agent,
        messages: [],
      }));
    }
  }, [apiClient]);

  const restartChat = useCallback(async () => {
    abortActiveStream();
    localStorage.removeItem('chat-session-id');
    setState(prev => ({
      ...prev,
      messages: [],
      sessionId: '',
      selectedAgent: null,
    }));
    
    try {
      const chats = await apiClient.getChats();
      setAvailableChats(chats);
    } catch (error) {
      console.error('Failed to refresh available chats:', error);
    }
  }, [abortActiveStream, apiClient]);

  const clearMessages = useCallback(async () => {
    localStorage.removeItem('chat-session-id');
    setState(prev => ({
      ...prev,
      messages: [],
      sessionId: '',
      selectedAgent: null,
    }));
    
    try {
      const chats = await apiClient.getChats();
      setAvailableChats(chats);
    } catch (error) {
      console.error('Failed to refresh available chats:', error);
    }
  }, [apiClient]);

  const switchChat = useCallback(async (sessionId: string) => {
    abortActiveStream();
    try {
      setIsSwitchingChat(true);
      setState(prev => ({ ...prev, messages: [] }));
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const chatData = await apiClient.getChatById(sessionId);
      let messages: Message[] = [];
      let selectedAgent: AgentType = null;
      
      if (chatData && chatData.messages && Array.isArray(chatData.messages)) {
        messages = transformMessages(chatData.messages);
        
        if (chatData.agentType) {
          selectedAgent = chatData.agentType as AgentType;
        } else if (messages.length > 0) {
          selectedAgent = 'analytics-stream' as AgentType;
        }
      }
      
      localStorage.setItem('chat-session-id', sessionId);
      setState(prev => ({
        ...prev,
        _id: chatData?._id || prev._id,
        sessionId,
        messages,
        selectedAgent,
      }));
      setSessionInitialized(true);
      setIsSwitchingChat(false);
    } catch (error) {
      console.error('Failed to switch chat:', error);
      localStorage.setItem('chat-session-id', sessionId);
      setState(prev => ({
        ...prev,
        _id: '',
        sessionId,
        messages: [],
        selectedAgent: null,
      }));
      setSessionInitialized(false);
      setIsSwitchingChat(false);
    }
  }, [abortActiveStream, transformMessages, apiClient]);

  const deleteChat = useCallback(async (sessionId: string) => {
    try {
      setDeletingChatId(sessionId);
      await apiClient.deleteChat(sessionId);
      
      const updatedChats = await apiClient.getChats();
      setAvailableChats(updatedChats);
      
      if (state.sessionId === sessionId) {
        abortActiveStream();
        localStorage.removeItem('chat-session-id');
        setState(prev => ({
          ...prev,
          messages: [],
          sessionId: '',
          selectedAgent: null,
        }));
        setSessionInitialized(false);
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    } finally {
      setDeletingChatId(null);
    }
  }, [state.sessionId, abortActiveStream, apiClient]);

  return {
    ...state,
    availableChats,
    isSwitchingChat,
    deletingChatId,
    streamingNodes,
    toggleChat,
    closeChat,
    sendMessage,
    selectAgent,
    restartChat,
    clearMessages,
    switchChat,
    deleteChat,
  };
}
